import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { AiChatMessage, AiChatMessageStatus, AiChatToolCall } from '../../core/types/data-models';

interface MessageDetailDialogProps {
  open: boolean;
  message: AiChatMessage | null;
  onClose: () => void;
}

const STATUS_COLORS: Record<AiChatMessageStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  STREAMING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function TextBlock({ label, content, copyable }: { label: string; content: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {copyable && (
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box
        component="pre"
        sx={{
          mt: 0.5,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 280,
          overflowY: 'auto',
        }}
      >
        {content}
      </Box>
    </Box>
  );
}

/** Parses `AiChatMessage.toolCalls`'s JSON-stringified array; empty on absence/malformed data. */
function parseToolCalls(raw: string | null): AiChatToolCall[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ToolCallBlock({ call }: { call: AiChatToolCall }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>{call.name}</Typography>
        {call.isError && <Chip icon={<ErrorOutlineIcon />} label="Error" size="small" color="error" variant="outlined" />}
        {call.requiresApproval && (
          <Chip label={call.decision ?? 'Requires approval'} size="small" color="warning" variant="outlined" />
        )}
      </Stack>
      <Box
        component="pre"
        sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
      >
        {prettyJson(call.input)}
      </Box>
      {call.output != null && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box
            component="pre"
            sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
          >
            {call.output}
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Read-only view of a single `AiChatMessage` — the "backend flow" behind one
 * turn: for a USER row, the fully composed system prompt actually sent to
 * the agent (`enrichedMessage`); for either role, the folded tool-call log
 * and token counts. No editing.
 */
export function MessageDetailDialog({ open, message, onClose }: MessageDetailDialogProps) {
  if (!message) return null;

  const toolCalls = parseToolCalls(message.toolCalls);
  const contextSnapshot = message.contextSnapshot ? prettyJson(JSON.parse(message.contextSnapshot)) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>{message.role === 'USER' ? 'User' : 'Assistant'} message #{message.id}</span>
          <Chip label={message.status} size="small" color={STATUS_COLORS[message.status]} variant="outlined" />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {message.error && <Alert severity="error">{message.error}</Alert>}

          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Field label="Created" value={new Date(message.createdAt).toLocaleString()} />
            <Field label="Model" value={message.modelId ?? '—'} />
            <Field label="Input tokens" value={message.inputTokens ?? '—'} />
            <Field label="Output tokens" value={message.outputTokens ?? '—'} />
          </Stack>

          <Divider />

          <TextBlock label={message.role === 'USER' ? 'Message' : 'Response'} content={message.content} copyable />

          {message.enrichedMessage && (
            <TextBlock
              label="Composed system prompt (this turn)"
              content={message.enrichedMessage}
              copyable
            />
          )}

          {toolCalls.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Tool calls ({toolCalls.length})
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {toolCalls.map((call) => (
                  <ToolCallBlock key={call.toolUseId} call={call} />
                ))}
              </Stack>
            </Box>
          )}

          {contextSnapshot && <TextBlock label="Context snapshot" content={contextSnapshot} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
